// MeetingPopover.jsx
import React from "react";
import { Popover } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import googleMeet from "../Images/google-meet-svgrepo-com.svg";
import msTeams from "../Images/microsoft-teams-svgrepo-com.svg";

/**
 * Props:
 *  - showMeetingPopup (bool)
 *  - meetingAnchorEl (element|null)
 *  - handleMeetingButtonClick (fn) -> receives (event)
 *  - handleMeetingPopupClose (fn)
 *  - setShowMeetingPopup (fn)
 *  - meetingCode (optional string) -> opens https://meet.google.com/<meetingCode> when provided
 *  - teamsJoinUrl (optional string) -> opens directly when provided
 */
const MeetingPopover = ({
  showMeetingPopup = false,
  meetingAnchorEl = null,
  handleMeetingButtonClick,
  handleMeetingPopupClose,
  setShowMeetingPopup,
  meetingCode,
  teamsJoinUrl,
}) => {
  // Use anchorEl if available, otherwise rely on showMeetingPopup boolean
  const open = Boolean(meetingAnchorEl) || Boolean(showMeetingPopup);

  const openUrlAndClose = (url) => {
    // Close popover cleanly
    if (typeof handleMeetingPopupClose === "function") handleMeetingPopupClose();
    if (typeof setShowMeetingPopup === "function") setShowMeetingPopup(false);

    // open in new tab safely
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleGoogle = () => {
    const url = meetingCode
      ? `https://meet.google.com/${encodeURIComponent(meetingCode)}`
      : "https://meet.google.com/new";
    openUrlAndClose(url);
  };

  const handleTeams = () => {
    const url = teamsJoinUrl
      ? teamsJoinUrl
      : "https://teams.microsoft.com/_#/calendarv2";
    openUrlAndClose(url);
  };

  return (
    <div className="flex items-center">
      {/* Floating + Button */}
      <button
        onClick={handleMeetingButtonClick}
        aria-label="Add a meeting"
        title="Add meeting"
        className="flex items-center justify-center w-14 h-14 rounded-full bg-white border border-gray-200
                   shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-200 ease-in-out focus:outline-none"
      >
        <AddIcon style={{ fontSize: 28, color: "#68548E", fontWeight: 700 }} />
      </button>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={meetingAnchorEl}
        onClose={handleMeetingPopupClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{
          style: {
            borderRadius: 12,
            padding: 8,
            boxShadow: "0px 10px 30px rgba(12,12,16,0.12)",
            minWidth: 220,
          },
        }}
      >
        <div className="flex flex-col">
          <button
            onClick={handleGoogle}
            className="flex items-center gap-3 px-4 py-3 text-[#68548E] font-semibold hover:bg-[#f3eafd] transition rounded-md text-left"
            aria-label="Open Google Meet"
          >
            <img src={googleMeet} alt="Google Meet" className="w-5 h-5" />
            <span>Google Meet</span>
          </button>

          <div className="border-t border-gray-100 my-1" />

          <button
            onClick={handleTeams}
            className="flex items-center gap-3 px-4 py-3 text-[#68548E] font-semibold hover:bg-[#f0f0f0] transition rounded-md text-left"
            aria-label="Open Microsoft Teams"
          >
            <img src={msTeams} alt="Microsoft Teams" className="w-5 h-5" />
            <span>Microsoft Teams</span>
          </button>
        </div>
      </Popover>
    </div>
  );
};

export default MeetingPopover;
